import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, dateTimeOf, label, money, timeOf, type BundlePick, type Order, type OrderEvent, type OrderLine, type OrderPizza, type PaymentsView, type Terminal } from '../lib/api';
import { useStoreEvents } from '../lib/events';
import { useBase, useScope } from '../scope';

function Pizza({ p, note }: { p: OrderPizza; note?: string }) {
  const adds = p.toppings.filter((t) => t.action === 'add');
  const removes = p.toppings.filter((t) => t.action === 'remove');
  return (
    <span className="sub">
      {note ? `${note}: ` : ''}{p.name} · {p.sizeName}{p.baseName ? ` · ${p.baseName}` : ''}
      {adds.length ? ` · + ${adds.map((t) => `${t.quantity > 1 ? `${t.quantity}× ` : ''}${t.toppingName}`).join(', ')}` : ''}
      {removes.length ? ` · no ${removes.map((t) => t.toppingName).join(', ')}` : ''}
      {p.modifiers.length ? ` · ${p.modifiers.map((m) => m.optionName).join(', ')}` : ''}
    </span>
  );
}

function Pick({ k }: { k: BundlePick }) {
  return (
    <>
      <span className="sub">
        {k.slotName}: {k.quantity > 1 ? `${k.quantity}× ` : ''}{k.name ?? k.pizza?.name ?? 'half & half'}
        {k.extraChargeInCents ? ` (+${money(k.extraChargeInCents)})` : ''}
        {k.modifiers?.length ? ` · ${k.modifiers.map((m) => m.optionName).join(', ')}` : ''}
      </span>
      {k.pizza && <Pizza p={k.pizza} />}
      {k.halfAndHalf && (
        <>
          <Pizza p={k.halfAndHalf.first} note="½" />
          <Pizza p={k.halfAndHalf.second} note="½" />
        </>
      )}
    </>
  );
}

function Line({ l }: { l: OrderLine }) {
  return (
    <div className="rowline" style={l.voidedAt ? { opacity: 0.55, textDecoration: 'line-through' } : undefined} data-line={l.orderItemId}>
      <span style={{ color: 'var(--ink-3)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{l.quantity}×</span>
      <div className="grow">
        <b>{l.name}{l.voidedAt ? ` · voided${l.voidReason ? ` (${label(l.voidReason)})` : ''}` : ''}</b>
        {l.comments && <span className="sub">“{l.comments}”</span>}
        {l.modifiers.length > 0 && <span className="sub">{l.modifiers.map((m) => `${m.modifierName}: ${m.optionName}`).join(' · ')}</span>}
        {l.pizza && <Pizza p={l.pizza} />}
        {l.halfAndHalf && (
          <>
            <Pizza p={l.halfAndHalf.first} note="½" />
            <Pizza p={l.halfAndHalf.second} note="½" />
          </>
        )}
        {l.picks?.map((k) => <Pick key={k.pickId} k={k} />)}
        {l.sentAt && <span className="sub">sent {l.sentAt.slice(11, 16)}</span>}
      </div>
      <span className="money" style={{ fontWeight: 700 }}>{money(l.lineTotalInCents)}</span>
    </div>
  );
}

/** One page for any channel: the frozen tree, payments & refunds, the
 * timeline, delivery state. Actions follow the order's state machine; a
 * terminal-owned order is read-only here except for transfer. */
export function OrderDetail() {
  const { rid, sid, store } = useScope();
  const base = useBase();
  const { orderId = '' } = useParams();
  const [order, setOrder] = useState<Order | null>(null);
  const [events, setEvents] = useState<OrderEvent[]>([]);
  const [payments, setPayments] = useState<PaymentsView | null>(null);
  const [terminals, setTerminals] = useState<Terminal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [payMethod, setPayMethod] = useState<'cash' | 'custom'>('cash');
  const [payAmount, setPayAmount] = useState('');
  const [customName, setCustomName] = useState('');

  const load = useCallback(async () => {
    try {
      const [o, ev, pay] = await Promise.all([
        api.get<Order>(`${base}/orders/${orderId}`),
        api.get<OrderEvent[]>(`${base}/orders/${orderId}/events`),
        api.get<PaymentsView>(`${base}/orders/${orderId}/payments`),
      ]);
      setOrder(o);
      setEvents(ev);
      setPayments(pay);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load.');
    }
  }, [base, orderId]);

  useEffect(() => {
    void load();
    api.get<Terminal[]>(`${base}/terminals`).then(setTerminals).catch(() => setTerminals([]));
  }, [load, base]);
  useStoreEvents(base, ['orders', 'payments'], () => void load());

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      await load();
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed.');
    } finally {
      setBusy(false);
    }
  }

  if (!order) return <div className="loading">{error ?? 'Loading…'}</div>;
  const o = order;
  const tz = store?.timezone;
  const live = ['new', 'accepted', 'in_progress'].includes(o.status);
  const onTerminal = o.owner !== 'server';
  const outstanding = payments?.outstandingInCents ?? 0;
  const amountCents = Math.round(parseFloat(payAmount || '0') * 100) || outstanding;

  return (
    <>
      <div className="page-head" style={{ alignItems: 'center', gap: 16 }}>
        <div>
          <div className="overline">
            <Link to={`/r/${rid}/s/${sid}/orders`}>Orders</Link> · {o.invoiceNumber ?? o.orderId} · placed {dateTimeOf(o.placedAt, tz)}
            {o.isEdited ? ` · edited (v${o.version})` : ''}{o.externalOrderId ? ` · ${o.externalOrderId}` : ''}
          </div>
          <h1 style={{ display: 'flex', gap: 14, alignItems: 'baseline' }}>
            <span className="docket">{o.docketLabel ? `#${o.docketLabel}` : 'Unaccepted'}</span>
            <span className={`chip chip-${o.status}`} style={{ fontSize: 14 }} data-status={o.status}>{label(o.status)}</span>
            <span className={`src src-${o.source}`}>{label(o.source)}</span>
            {onTerminal && <span className="chip" title={`Owned by terminal ${o.owner}`}>on {terminals.find((t) => t.terminalId === o.owner)?.name ?? 'a terminal'}</span>}
          </h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {o.status === 'new' && (
            <>
              <button className="btn btn-primary" disabled={busy}
                onClick={() => {
                  const eta = prompt('ETA in minutes?', '15');
                  if (eta !== null) void act(() => api.post(`${base}/orders/${orderId}/accept`, { etaMinutes: Number(eta) || null }));
                }}>
                Accept
              </button>
              <button className="btn btn-danger" disabled={busy}
                onClick={() => {
                  const reason = prompt('Reason for rejecting?');
                  if (reason) void act(() => api.post(`${base}/orders/${orderId}/reject`, { reason }));
                }}>
                Reject
              </button>
            </>
          )}
          {o.status === 'accepted' && !onTerminal && (
            <button className="btn btn-primary" disabled={busy} onClick={() => void act(() => api.post(`${base}/orders/${orderId}/complete`))}>
              Complete
            </button>
          )}
          {o.status === 'completed' && (
            <button className="btn btn-outline" disabled={busy} title="Record the hand-over to the customer or courier"
              onClick={() => void act(() => api.post(`${base}/orders/${orderId}/hand-off`))}>
              Hand off
            </button>
          )}
          {live && !onTerminal && (
            <button className="btn btn-outline" disabled={busy}
              onClick={() => {
                const reason = prompt('Reason for cancelling?');
                if (reason !== null) void act(() => api.post(`${base}/orders/${orderId}/cancel`, { reason: reason || null }));
              }}>
              Cancel
            </button>
          )}
          {live && terminals.length > 0 && (
            <select className="input" style={{ width: 'auto' }} aria-label="Transfer to terminal" disabled={busy} value=""
              onChange={(e) => {
                const terminalId = e.target.value;
                if (terminalId) void act(() => api.post(`${base}/orders/${orderId}/transfer`, { terminalId, force: !!onTerminal }));
              }}>
              <option value="">Transfer to…</option>
              {terminals.filter((t) => t.active && t.terminalId !== o.owner).map((t) => (
                <option key={t.terminalId} value={t.terminalId}>{t.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}
      {o.statusReason && <div className="mn-hint" style={{ marginBottom: 12 }}>Reason: {o.statusReason}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="panel" data-section="lines">
            <h2>Items</h2>
            {o.lines.map((l) => <Line key={l.orderItemId} l={l} />)}
            {o.discounts.map((d) => (
              <div key={d.rowId} className="rowline">
                <div className="grow"><b>Discount · {d.label}</b><span className="sub">{label(d.discountType)}{d.couponCode ? ` · ${d.couponCode}` : ''}</span></div>
                <span className="money" style={{ fontWeight: 700 }}>−{money(d.amountInCents)}</span>
              </div>
            ))}
            {o.surcharges.map((s) => (
              <div key={s.rowId} className="rowline">
                <div className="grow"><b>Surcharge · {s.label}</b><span className="sub">{label(s.surchargeType)}</span></div>
                <span className="money" style={{ fontWeight: 700 }}>{money(s.amountInCents)}</span>
              </div>
            ))}
            {o.deliveryFeeInCents > 0 && (
              <div className="rowline">
                <div className="grow"><b>Delivery fee</b></div>
                <span className="money" style={{ fontWeight: 700 }}>{money(o.deliveryFeeInCents)}</span>
              </div>
            )}
            <div className="rowline" style={{ borderTop: '2px solid var(--ink)', marginTop: 4 }}>
              <div className="grow">
                <b>Total{o.gstInCents > 0 ? ` · incl. ${money(o.gstInCents)} GST` : ''}</b>
                <span className="sub">subtotal {money(o.subtotalInCents)}</span>
              </div>
              <span className="money" style={{ fontWeight: 800, fontSize: 16 }} data-total>{money(o.grandTotalInCents)}</span>
            </div>
          </div>

          <div className="panel" data-section="payments">
            <h2>Payments · <span data-payment-state={payments?.paymentState}>{payments ? label(payments.paymentState) : '…'}</span></h2>
            {payments?.payments.length === 0 && (
              <div className="loading">{o.isPayLater ? 'Pay later — unpaid by design.' : 'No payments yet.'}</div>
            )}
            {payments?.payments.map((p) => (
              <div key={p.paymentId} className="rowline">
                <div className="grow">
                  <b style={{ textTransform: 'capitalize' }}>
                    {p.txnType === 'payment' ? (p.customPaymentName ?? p.method) : `${p.txnType} (${p.customPaymentName ?? p.method})`}
                    {p.provider && p.provider !== 'manual' ? ` · ${label(p.provider)}` : ''}
                    {p.cardLast4 ? ` · ${p.cardBrand ?? 'card'} ····${p.cardLast4}` : ''}
                  </b>
                  <span className="sub">
                    {timeOf(p.occurredAt, tz)}
                    {p.tipInCents > 0 ? ` · ${money(p.tipInCents)} tip` : ''}
                    {p.reason ? ` · ${p.reason}` : ''}
                  </span>
                </div>
                <span className={`chip chip-${p.status === 'success' ? 'completed' : p.status}`}>{p.status}</span>
                <span className="money" style={{ fontWeight: 700 }}>
                  {p.txnType === 'payment' ? '' : '−'}{money(p.amountInCents)}
                </span>
              </div>
            ))}
            {payments && (
              <div className="rowline">
                <div className="grow"><b>{outstanding > 0 ? 'Outstanding' : 'Refundable'}</b></div>
                <span className="money" style={{ fontWeight: 800 }} data-outstanding>{money(outstanding > 0 ? outstanding : payments.refundableInCents)}</span>
              </div>
            )}
            {payments && live && !onTerminal && outstanding > 0 && (
              <div className="mn-inline-form" style={{ paddingTop: 12 }}>
                <select className="input" style={{ width: 'auto' }} aria-label="Payment method" value={payMethod} onChange={(e) => setPayMethod(e.target.value as 'cash' | 'custom')}>
                  <option value="cash">Cash</option>
                  <option value="custom">Other tender</option>
                </select>
                {payMethod === 'custom' && (
                  <input className="input" style={{ width: 140 }} placeholder="Tender name" aria-label="Tender name" value={customName} onChange={(e) => setCustomName(e.target.value)} />
                )}
                <input className="input money" style={{ width: 110 }} inputMode="decimal" aria-label="Amount" placeholder={(outstanding / 100).toFixed(2)} value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
                <button className="btn btn-primary btn-sm" disabled={busy || amountCents <= 0 || (payMethod === 'custom' && !customName.trim())}
                  onClick={() => void act(async () => {
                    await api.post(`${base}/orders/${orderId}/payments`, { method: payMethod, amountInCents: amountCents, customPaymentName: payMethod === 'custom' ? customName.trim() : null });
                    setPayAmount('');
                  })}>
                  Record payment
                </button>
              </div>
            )}
            {payments && !onTerminal && payments.refundableInCents > 0 && (
              <div style={{ paddingTop: 8 }}>
                <button className="btn btn-outline btn-sm" disabled={busy}
                  onClick={() => {
                    const amt = prompt(`Refund how much (up to ${money(payments.refundableInCents)})?`, (payments.refundableInCents / 100).toFixed(2));
                    if (amt === null) return;
                    const cents = Math.round(parseFloat(amt) * 100);
                    const reason = prompt('Reason?') ?? '';
                    if (cents > 0) void act(() => api.post(`${base}/orders/${orderId}/refunds`, { method: 'cash', amountInCents: cents, reason: reason || null }));
                  }}>
                  Refund in cash
                </button>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="panel" data-section="details">
            <h2>Details</h2>
            <div className="rowline"><div className="grow"><b>Customer</b>
              <span className="sub">{o.customerName ?? 'Guest'}{o.customerPhoneE164 ? ` · ${o.customerPhoneE164}` : ''}</span></div></div>
            <div className="rowline"><div className="grow"><b>Type</b>
              <span className="sub">
                {o.collectionType.replace('_', '-')}
                {o.dineIn ? ` · table ${o.dineIn.tableName}${o.dineIn.guestCount ? ` · ${o.dineIn.guestCount} guests` : ''}` : ''}
                {o.requestedFor ? ` · for ${dateTimeOf(o.requestedFor, tz)}` : ' · ASAP'}
              </span></div></div>
            {o.delivery && (
              <div className="rowline"><div className="grow"><b>Delivery</b>
                <span className="sub">{o.delivery.addressLine}, {o.delivery.suburb} {o.delivery.state} {o.delivery.postcode}</span>
                <span className="sub">
                  {label(o.delivery.courier)}{o.delivery.courierStatus ? ` · ${label(o.delivery.courierStatus)}` : ''}
                  {o.delivery.trackingUrl ? <> · <a href={o.delivery.trackingUrl} target="_blank" rel="noreferrer">track</a></> : null}
                </span></div></div>
            )}
            {o.comments && (
              <div className="rowline"><div className="grow"><b>Notes</b><span className="sub">{o.comments}</span></div></div>
            )}
            <div className="rowline"><div className="grow"><b>Business date</b><span className="sub">{o.businessDate}{o.employeeId ? ` · staff ${o.employeeId}` : ''}</span></div></div>
          </div>

          <div className="panel" data-section="timeline">
            <h2>Timeline</h2>
            {events.map((e) => (
              <div key={e.seq} className="rowline">
                <span className="tm">{timeOf(e.occurredAt, tz)}</span>
                <div className="grow">
                  <b style={{ textTransform: 'capitalize' }}>{label(e.eventType)}</b>
                  <span className="sub">
                    {e.actorType}{e.epoch > 1 ? ` · epoch ${e.epoch}` : ''}{e.note ? ` · ${e.note}` : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
